<template>
  <div class="category-tree-container">
    <p class="active-node-label">{{ activeNodeLabel }}</p>
    <ul>
      <UiTree
        v-for="category in categoryTree"
        :key="category.id"
        :node="category"
        @clicked="onNodeClick"
      />
    </ul>
  </div>
</template>

<script lang="ts" setup>
import UiTree from '@sephora-asia/ui-tree'
import type { CategoryFilters } from '~/types/api/data/product/filter/filter.types'
import type { CategoryTree } from '~/types/stores/product-listing/filters-store.types'
import {
  capitalizeEachWord,
  removeDashOrUnderscore,
} from '~/utils/string-helpers'
import {
  filterByParentId,
  filterBySlugUrlThatIsTopCategory,
  filterByTopCategoryRouteParam,
} from '~/utils/product-filter/category-tree-helpers'

const props = defineProps<{
  categoryFilters: CategoryFilters[]
  slugTitle: string
}>()

const route = useRoute()
const { baseDirectory, slugUrl } = useBaseRoutes()
const { getCategoryPathFromRoute } = useProductListingRoutes()
const { setSelectedCategory } = useProductListingFiltersStore()

const activeTreeNodeLabel = ref<string>('')

const activeNodeLabel = computed(() => {
  if (props.slugTitle) {
    return capitalizeEachWord(removeDashOrUnderscore(props.slugTitle))
  }
  return activeTreeNodeLabel
})

const categoryTree = computed(() => {
  const initialFilterFn = slugUrl.value
    ? filterBySlugUrlThatIsTopCategory()
    : filterByTopCategoryRouteParam(route.params.topCategory.toString())
  return getCategoryTree(
    props.categoryFilters,
    getCategoryPathFromRoute().toLowerCase(),
    initialFilterFn
  )
})

function getCategoryTree(
  categories: CategoryFilters[],
  currentCategoryUrl: string,
  filterFn: (filter: CategoryFilters) => boolean
) {
  const parentNodes = categories.filter(filterFn)
  const parentNodeIds = parentNodes.map(node => node.id)
  const childNodes = categories.reduce((prev: CategoryFilters[], curr) => {
    if (parentNodeIds.includes(curr.id)) {
      return prev
    }
    return [...prev, curr]
  }, [])
  return parentNodes.map((node: CategoryFilters): CategoryTree => {
    const isActive = node.slugUrl === currentCategoryUrl
    if (isActive) {
      activeTreeNodeLabel.value = node.label
    }
    // generate children nodes only if current node is active
    return {
      ...node,
      linkUrl: `/${baseDirectory.value}/${node.slugUrl}`,
      isActive,
      children: currentCategoryUrl.includes(node.slugUrl)
        ? getCategoryTree(
            childNodes,
            currentCategoryUrl,
            filterByParentId(node.id)
          )
        : [],
    }
  })
}

function onNodeClick(node: CategoryTree) {
  setSelectedCategory(node.slugUrl)
}
</script>

<style lang="scss" scoped>
.category-tree-container {
  padding: 0px 20px;
  .active-node-label {
    @extend .headline-m;
    margin-bottom: 12px;
  }
}
</style>
